import { useQuery } from '@tanstack/react-query';
import counselingService, { type CounselingRecord } from '../../services/counselingService';
import { isAcknowledgmentCounselingRecord } from '../../constants/counselingConstants';

export type { CounselingRecord };

/**
 * Every counseling record for a patient, cached and deduped by TanStack Query.
 *
 * GP-1358 — the Rx Detail dialog needs the counseling for the prescription it is
 * showing, and the patient chart already refetches `/api/counseling/by-patient`
 * on nearly every tab switch. Going through one keyed query means opening the
 * dialog reuses that cache instead of adding another round-trip.
 */
export function usePatientCounselingRecords(
  patientId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<CounselingRecord[]>({
    queryKey: ['patient', 'counseling', patientId],
    queryFn: () => counselingService.getRecordsByPatient(patientId!),
    enabled: !!patientId && (options?.enabled ?? true),
    staleTime: 2 * 60 * 1000,
  });
}

/** The record shape both selectors below need — nothing more. */
type CounselingRecordLike = {
  rxNumber?: string | number | null;
  createdAt?: string | null;
  reason?: string | null;
};

/**
 * The counseling records recorded against ANY of the given Rx numbers, newest
 * first.
 *
 * GP-1368 — a medication that has been filled thirty times is thirty Rx numbers
 * in one Kroll series, and the counseling sits on whichever fill it was done
 * on. Asking only the fill in hand reported "no counseling record" for a
 * medication that HAD been counselled, so the caller can pass the whole series
 * (`rx-detail` → `rxDetails`) and be told which Rx holds the record.
 *
 * Clinical-verification acknowledgments (GP-1155) are dropped: they are
 * persisted as counseling rows but are not counseling sessions, and this data
 * prints on the hardcopy, where a blank "Completed" would misrepresent one.
 */
export function filterCounselingForRxNumbers<T extends CounselingRecordLike>(
  records: T[] | undefined | null,
  rxNumbers: Array<string | number | null | undefined>,
): T[] {
  const wanted = new Set(
    rxNumbers
      .filter((n) => n !== null && n !== undefined && String(n) !== '')
      .map((n) => String(n)),
  );
  if (!records?.length || wanted.size === 0) return [];
  return records
    .filter((r) => r.rxNumber != null && wanted.has(String(r.rxNumber)))
    .filter((r) => !isAcknowledgmentCounselingRecord(r))
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
}

/**
 * The counseling records that belong to ONE prescription, newest first.
 *
 * Records carrying a different Rx number are never borrowed — a counseling
 * session documents the drug it was about, and this is what prints on that Rx's
 * hardcopy. Use {@link filterCounselingForRxNumbers} to look across the fill
 * series instead (GP-1368), where each record stays labelled with its own Rx.
 */
export function filterCounselingForRx<T extends CounselingRecordLike>(
  records: T[] | undefined | null,
  rxNumber: string | number | null | undefined,
): T[] {
  return filterCounselingForRxNumbers(records, [rxNumber]);
}
